import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { getBlockColor, getEmissiveColor } from './materials/SpectrumMaterial';

// Shared geometry - created once, reused for all blocks
const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = BLOCK_SIZE * 0.75;
const sharedBlockGeometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH);
const sharedGlowGeometry = new THREE.BoxGeometry(BLOCK_SIZE * 1.2, BLOCK_SIZE * 1.2, BLOCK_DEPTH * 0.6);
const sharedPlaneGeometry = new THREE.PlaneGeometry(0.7, 0.7);
const sharedEdgeGeometry = new THREE.EdgesGeometry(new THREE.BoxGeometry(BLOCK_SIZE * 1.02, BLOCK_SIZE * 1.02, BLOCK_DEPTH * 0.82));

// Corner accent lights
const CornerGlows = ({ color, size }) => {
  const corners = useMemo(() => {
    const half = size * 0.45;
    return [
      [-half, -half],
      [-half, half],
      [half, -half],
      [half, half],
    ];
  }, [size]);

  return (
    <>
      {corners.map((pos, i) => (
        <mesh key={i} position={[pos[0], pos[1], 0.4]}>
          <circleGeometry args={[0.06, 8]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.8}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </>
  );
};

const TetrisBlock3D = ({
  position,
  colorClass,
  isGhost = false,
  isActive = false,
  animate = true,
  scale = 1
}) => {
  const groupRef = useRef();
  const innerGlowRef = useRef();
  const outerGlowRef = useRef();
  const edgeRef = useRef();
  const baseColor = getBlockColor(colorClass);
  const emissiveColor = getEmissiveColor(colorClass);

  // Single consolidated useFrame for all animations in this block
  useFrame((state) => {
    const time = state.clock.elapsedTime;

    // Scale pulse for active pieces only
    if (groupRef.current && animate && isActive) {
      const pulse = Math.sin(time * 4) * 0.02 + 1;
      groupRef.current.scale.setScalar(pulse);
    }

    // Glow animations for non-ghost blocks
    if (!isGhost) {
      const glowPulse = isActive
        ? 0.5 + Math.sin(time * 3) * 0.25
        : 0.35 + Math.sin(time * 1.5) * 0.1;
      const innerPulse = isActive
        ? 0.4 + Math.sin(time * 3) * 0.2
        : 0.25 + Math.sin(time * 1.5) * 0.1;
      const edgePulse = isActive
        ? 0.7 + Math.sin(time * 4) * 0.3
        : 0.5 + Math.sin(time * 2) * 0.15;

      if (outerGlowRef.current?.material) {
        outerGlowRef.current.material.opacity = glowPulse;
      }
      if (innerGlowRef.current?.material) {
        innerGlowRef.current.material.opacity = innerPulse;
      }
      if (edgeRef.current?.material) {
        edgeRef.current.material.opacity = edgePulse;
      }
    }
  });

  // Main material - vibrant with strong emission
  const material = useMemo(() => {
    if (isGhost) {
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(baseColor),
        emissive: new THREE.Color(baseColor),
        emissiveIntensity: 0.3,
        metalness: 0.1,
        roughness: 0.7,
        opacity: 0.3,
        transparent: true,
        side: THREE.DoubleSide,
      });
    }

    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(baseColor),
      emissive: new THREE.Color(emissiveColor),
      emissiveIntensity: isActive ? 1.0 : 0.7,
      metalness: 0.4,
      roughness: 0.15,
      side: THREE.DoubleSide,
    });
  }, [baseColor, emissiveColor, isGhost, isActive]);

  // Outer glow material - soft bloom effect
  const outerGlowMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color: new THREE.Color(baseColor),
      opacity: 0.4,
      transparent: true,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
  }, [baseColor]);

  const blockScale = scale;

  return (
    <group position={position} ref={groupRef} scale={blockScale}>
      {/* Main block body - using shared geometry */}
      <mesh
        geometry={sharedBlockGeometry}
        material={material}
        castShadow={!isGhost}
        receiveShadow={!isGhost}
      />

      {/* Outer glow halo */}
      {!isGhost && (
        <mesh ref={outerGlowRef} geometry={sharedGlowGeometry} material={outerGlowMaterial} />
      )}

      {/* Inner soft glow */}
      {!isGhost && (
        <mesh ref={innerGlowRef} position={[0, 0, 0.35]} geometry={sharedPlaneGeometry}>
          <meshBasicMaterial
            color={baseColor}
            transparent
            opacity={0.3}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Glowing neon edge */}
      {!isGhost && (
        <lineSegments ref={edgeRef} geometry={sharedEdgeGeometry}>
          <lineBasicMaterial
            color={baseColor}
            linewidth={2}
            opacity={0.6}
            transparent
            blending={THREE.AdditiveBlending}
          />
        </lineSegments>
      )}

      {/* Corner accent lights for extra pop */}
      {!isGhost && isActive && (
        <CornerGlows color={baseColor} size={BLOCK_SIZE} />
      )}
    </group>
  );
};

export default TetrisBlock3D;
